// 知识库中间的列表：全部资料、某个分区的全部、搜索结果。一行一篇：标题、分区 · 来源 · 收进时间，解析中的标出来。
// 能按类型、解析状态筛；资料多的时候往下加载。

import { useInfiniteQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Dropdown, type MenuProps } from 'antd'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { listKnowledge, type KnowledgeQuery } from '../../api/kb'
import { Pill } from '../../ui'
import { docTitle, parseState, shortDate, sourceLabel } from './DocCard'
import type { FolderSel } from './KbTree'

const PAGE = 40
const TYPES: [string, string][] = [['', '全部类型'], ['pdf', 'PDF'], ['md', 'Markdown'], ['docx', 'Word'], ['url', '网页'], ['manual', '手写']]
const STATUSES: [string, string][] = [['', '全部状态'], ['completed', '已解析'], ['processing', '正在解析'], ['failed', '解析失败']]

export function DocList({ kbId, folder, keyword }: { kbId: string; folder: FolderSel; keyword: string }) {
  const [fileType, setFileType] = useState('')
  const [status, setStatus] = useState('')
  const query: KnowledgeQuery = { keyword, folderPath: folder, recursive: folder !== undefined && folder !== '', fileType, parseStatus: status, pageSize: PAGE }
  const docs = useInfiniteQuery({
    queryKey: ['knowledge', kbId, query],
    queryFn: ({ pageParam }) => listKnowledge(kbId, { ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.page_size < last.total ? last.page + 1 : undefined),
    // 有资料在解析时，隔一会儿刷新一次
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.data.some((d) => parseState(d.parse_status) === 'parsing')) ? 8000 : false),
  })
  const items = docs.data?.pages.flatMap((p) => p.data) ?? []
  const total = docs.data?.pages[0]?.total ?? 0
  const where = keyword ? `搜索「${keyword}」` : folder === undefined ? '全部资料' : folder === '' ? '根目录' : folder.split('/').pop()!
  const menu = (opts: [string, string][], value: string, set: (v: string) => void): MenuProps => ({
    items: opts.map(([k, label]) => ({ key: k || '_', label })),
    selectable: true,
    selectedKeys: [value || '_'],
    onClick: ({ key }) => set(key === '_' ? '' : key),
  })

  return (
    <section className="dl" aria-label={where}>
      <header className="dl-head">
        <h2>{where}</h2>
        {docs.isSuccess && <span className="n">{total} 篇</span>}
        <span className="grow" />
        <Dropdown trigger={['click']} menu={menu(TYPES, fileType, setFileType)}>
          <Pill size="sm" variant={fileType ? 'selected' : 'ghost'}>
            {TYPES.find(([k]) => k === fileType)?.[1]}
            <ChevronDown width={14} height={14} />
          </Pill>
        </Dropdown>
        <Dropdown trigger={['click']} menu={menu(STATUSES, status, setStatus)}>
          <Pill size="sm" variant={status ? 'selected' : 'ghost'}>
            {STATUSES.find(([k]) => k === status)?.[1]}
            <ChevronDown width={14} height={14} />
          </Pill>
        </Dropdown>
      </header>
      {docs.isError && <p className="dl-note">没能载入资料：{docs.error.message}</p>}
      {docs.isSuccess && items.length === 0 && <p className="dl-note">{keyword || fileType || status ? '没有符合条件的资料' : '这里还没有资料'}</p>}
      <ul className="dl-rows" aria-busy={docs.isPending}>
        {items.map((d) => {
          const state = parseState(d.parse_status)
          return (
            <li key={d.id}>
              <Link to={`/kb/${kbId}/d/${d.id}`} className="dl-row">
                <span className="t">{docTitle(d)}</span>
                <span className="m">
                  {folder === undefined && d.folder_path ? `${d.folder_path.split('/').pop()} · ` : ''}
                  {sourceLabel(d)} · {shortDate(d.created_at)}
                  {state !== 'ready' && <span className="st" data-state={state}>{state === 'parsing' ? '正在解析' : '解析失败'}</span>}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
      <div className="dl-foot">
        {docs.hasNextPage ? (
          <Pill onClick={() => docs.fetchNextPage()} disabled={docs.isFetchingNextPage}>
            再看 {Math.min(PAGE, total - items.length)} 篇
          </Pill>
        ) : (
          items.length > 0 && <span>共 {total} 篇</span>
        )}
      </div>
    </section>
  )
}
